import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import axios from "axios";
import swal from "sweetalert";
import { t } from "i18next";
import { 
    Box, Button, TextField, Typography, InputAdornment, IconButton, 
    CircularProgress, Alert, Stack 
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import FacebookIcon from '@mui/icons-material/Facebook';
import GoogleIcon from '@mui/icons-material/Google';
import LinkedInIcon from '@mui/icons-material/LinkedIn';

// ==========================================
// THEME: Diprella Style
// ==========================================
const customTheme = createTheme({
    palette: {
        primary: { main: "#3caea3" }, 
        secondary: { main: "#f6ad55" }, 
        error: { main: "#e53e3e" }, 
        background: { default: "#f6f7fb" },
    },
    typography: { fontFamily: "'Montserrat', 'Inter', sans-serif" },
});

function Login() {
    const [err, setErr] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    
    const login = localStorage.getItem("user");
    const navigate = useNavigate();
    
    const {
        register,
        handleSubmit,
        setError,
        formState: { errors }
    } = useForm();

    // =================================================================
    // MAGIC TRICK: CHẶN CSS TỪ INDEX.CSS BẰNG CÁCH LỘT BỎ CLASS .APP
    // =================================================================
    useEffect(() => {
        // 1. Tìm thẻ cha đang chứa class "App" (thủ phạm gây vỡ giao diện)
        const appWrapper = document.querySelector('.App');
        
        // 2. Nếu tìm thấy, gỡ bỏ class "App" đi để CSS trong index.css mất tác dụng
        if (appWrapper) {
            appWrapper.classList.remove('App');
            appWrapper.classList.add('login-safe-zone'); // Đặt tạm 1 class an toàn
        }

        // 3. Khóa cứng body để mất cái màu nền đỏ sẫm (#3d0101) của bạn
        document.body.style.margin = "0";
        document.body.style.padding = "0";
        document.body.style.backgroundColor = "#f6f7fb";

        // 4. CLEANUP: Khi đăng nhập xong và rời khỏi trang Login, trả lại class cũ
        return () => {
            const safeZone = document.querySelector('.login-safe-zone');
            if (safeZone) {
                safeZone.classList.remove('login-safe-zone');
                safeZone.classList.add('App'); // Trả lại class App cho các trang bên trong
            }
            document.body.style.backgroundColor = ""; // Xóa màu nền tạm
        };
    }, []);

    useEffect(() => {
        if (login) {
            navigate("/admin");
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [login]);

    const onSubmit = async (data) => {
        setErr(null);
        const regex = /^[a-zA-Z0-9]+$/;
        
        if (data.username.length < 6) {
            setError("username", { type: "minLength", message: "Tên đăng nhập tối thiểu 6 kí tự" });
            return;
        }
        if (!regex.test(data.username)) {
            setError("username", { type: "pattern", message: "Tên đăng nhập không chứa ký tự đặc biệt" });
            return;
        }
        if (data.password.length < 6) {
            setError("password", { type: "minLength", message: "Mật khẩu tối thiểu 6 kí tự" });
            return;
        }

        setLoading(true);
        axios.post(`${process.env.REACT_APP_API_URL}/auth/login`, {
            username: data.username.toLowerCase(),
            password: data.password
        })
        .then((res) => {
            swal({
                title: "Đăng nhập thành công!",
                text: "Chào mừng bạn quay trở lại hệ thống.",
                icon: "success",
                buttons: "VÀO HỆ THỐNG",
                timer: 2000,
            }).then(() => {
                localStorage.removeItem("user");
                localStorage.removeItem("profile");
                localStorage.removeItem("data");
                localStorage.setItem("user", res.data.data);
                
                navigate("/admin");
            });
        })
        .catch((error) => {
            setLoading(false);
            setErr(error.response?.data?.message ? error.response.data.message : "Tên đăng nhập hoặc mật khẩu không chính xác!");
        });
    };

    const toggleShowPassword = () => setShowPassword(!showPassword);

    const inputSx = {
        '& .MuiOutlinedInput-root': {
            backgroundColor: '#f1f3f8',
            borderRadius: '10px',
            border: 'none',
            height: '52px',
            '& fieldset': { border: 'none' },
            '&:hover fieldset': { border: 'none' },
            '&.Mui-focused fieldset': { border: '1px solid #3caea3' },
        },
        '& .MuiInputBase-input': { 
            fontSize: '0.95rem',
            padding: '0 10px',
            height: '100%',
        }
    };

    return (
        <ThemeProvider theme={customTheme}>
            {/* Đè thêm 1 lớp style nội tuyến để chắc chắn không bị dính CSS rác */}
            <style>{`
                .login-safe-zone * { box-sizing: border-box; text-align: left; }
                .login-safe-zone h3, .login-safe-zone h4, .login-safe-zone p, .login-safe-zone button, .login-safe-zone span { text-align: center; }
                .login-safe-zone input { background: transparent; border: none; box-shadow: none; margin: 0; padding: 0 10px; width: 100%; height: 100%; }
                .login-safe-zone form { margin: 0; padding: 0; width: 100%; max-width: 380px; }
            `}</style>

            <Box sx={{ 
                minHeight: '100vh', width: '100vw', display: 'flex', alignItems: 'center', justifyContent: 'center', 
                backgroundColor: 'background.default', position: 'relative', overflow: 'hidden', p: 2, m: 0
            }}>
                
                {/* HÌNH TRANG TRÍ GÓC BACKGROUND */}
                <Box sx={{ 
                    position: 'absolute', top: 0, right: 0, width: 0, height: 0, zIndex: 0,
                    borderStyle: 'solid', borderWidth: '0 250px 250px 0', borderColor: 'transparent #e53e3e transparent transparent'
                }} />
                <Box sx={{ 
                    position: 'absolute', bottom: 0, left: 0, width: '180px', height: '180px', 
                    backgroundColor: '#f6ad55', borderRadius: '30px 0 0 0', zIndex: 0
                }} />

                {/* KHUNG MAIN CHÍNH */}
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', md: 'row' }, 
                    width: '100%', 
                    maxWidth: '950px', 
                    backgroundColor: '#fff', 
                    borderRadius: '20px', 
                    boxShadow: '0 15px 35px rgba(0,0,0,0.1)', 
                    zIndex: 1, 
                    overflow: 'hidden', 
                    minHeight: { xs: 'auto', md: '550px' }
                }}>
                    
                    {/* ================= LEFT PANEL (FORM LOGIN) ================= */}
                    <Box sx={{ 
                        flex: { xs: 1, md: 0.55 }, 
                        p: { xs: 4, sm: 6 }, 
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' 
                    }}>
                        
                        {/* Logo */}
                     

                        <Typography variant="h4" fontWeight="800" color="primary.main" mb={3}>
                          Login to Admin
                        </Typography>

                        {/* Social Icons */}
                        <Stack direction="row" spacing={2} mb={3}>
                            <IconButton sx={{ p: 1.5, border: '1px solid #ddd', borderRadius: '50%', '&:hover': { borderColor: 'primary.main', color: 'primary.main' } }}>
                                <FacebookIcon />
                            </IconButton>
                            <IconButton sx={{ p: 1.5, border: '1px solid #ddd', borderRadius: '50%', '&:hover': { borderColor: 'primary.main', color: 'primary.main' } }}>
                                <GoogleIcon />
                            </IconButton>
                            <IconButton sx={{ p: 1.5, border: '1px solid #ddd', borderRadius: '50%', '&:hover': { borderColor: 'primary.main', color: 'primary.main' } }}>
                                <LinkedInIcon />
                            </IconButton>
                        </Stack>

                        <Typography variant="body2" color="text.secondary" mb={4}>
                            Đăng nhập tài khoản
                        </Typography>

                        {/* Báo lỗi tổng */}
                        {err && <Alert severity="error" sx={{ width: '100%', mb: 2, borderRadius: '8px' }}>{err}</Alert>}

                        {/* FORM */}
                        <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%', maxWidth: '380px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Box sx={{ mb: 2.5, width: '100%' }}>
                                <TextField
                                    fullWidth
                                    {...register("username", { required: true })}
                                    placeholder="Tên đăng nhập"
                                    error={!!errors.username}
                                    helperText={errors.username ? errors.username.message : ""}
                                    sx={inputSx}
                                    disabled={loading}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <PersonOutlineOutlinedIcon sx={{ color: '#999', mr: 1 }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>

                            <Box sx={{ mb: 2, width: '100%' }}>
                                <TextField
                                    fullWidth
                                    type={showPassword ? "text" : "password"}
                                    {...register("password", { required: true })}
                                    placeholder="Mật khẩu"
                                    error={!!errors.password}
                                    helperText={errors.password ? errors.password.message : ""}
                                    sx={inputSx}
                                    disabled={loading}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <LockOutlinedIcon sx={{ color: '#999', mr: 1 }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={toggleShowPassword} edge="end" sx={{ color: '#999' }}>
                                                    {showPassword ? <Visibility /> : <VisibilityOff />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Box>

                          
                            <Box sx={{ width: '100%' }}>
                                <Button 
                                    type="submit" 
                                    variant="contained" 
                                    disabled={loading}
                                    sx={{ 
                                        borderRadius: '30px', py: 1.5, px: 6, fontSize: '1rem', fontWeight: 700, 
                                        boxShadow: 'none', textTransform: 'uppercase', letterSpacing: '1px',
                                        backgroundColor: '#3caea3', color: '#fff', width: 'auto',
                                        '&:hover': { backgroundColor: '#31978d', boxShadow: '0 4px 12px rgba(60,174,163,0.3)' }
                                    }}
                                >
                                    {loading ? <CircularProgress size={24} color="inherit" /> : "ĐĂNG NHẬP"}
                                </Button>
                            </Box>
                        </form>

                    </Box>

                    {/* ================= RIGHT PANEL (WELCOME) ================= */}
                    <Box sx={{ 
                        flex: { xs: 1, md: 0.45 }, 
                        backgroundColor: '#3caea3', 
                        color: '#fff', 
                        display: { xs: 'none', md: 'flex' }, 
                        flexDirection: 'column', justifyContent: 'center', alignItems: 'center', 
                        p: 6, textAlign: 'center',
                        backgroundImage: `radial-gradient(circle at 80% 30%, rgba(255,255,255,0.1) 5%, transparent 10%),
                                          radial-gradient(circle at 20% 70%, rgba(255,255,255,0.05) 10%, transparent 20%)`,
                        backgroundSize: '150px 150px'
                    }}>
                        <Typography variant="h3" fontWeight="800" mb={3} sx={{ color: '#fff' }}>
                            Hello, Friend!
                        </Typography>
                        <Typography variant="body1" mb={5} sx={{ lineHeight: 1.6, opacity: 0.9, color: '#fff' }}>
                            Enter your personal details and start journey with us
                        </Typography>
                       
                    </Box>

                </Box>
            </Box>
        </ThemeProvider>
    );
}

export default Login;